import { assertNotEmpty, notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback } from 'react'

import { useOperationsContext } from '../../workspace/operationsContext'
import { groupSearchResults } from './helpers'
import type {
  ConfigurationItemSearchFunctions,
  ConfigurationItemSearchResult,
} from './types'

export const useRegisteredTenantsSearch =
  (): ConfigurationItemSearchFunctions => {
    const { registeredTenantCreateOperations } = useOperationsContext()
    const translate = useTranslate()

    const searchByName = useCallback(
      (query: string) => {
        const needle = query.toLowerCase().trim()

        return Promise.resolve(
          Array.from(registeredTenantCreateOperations.values()).flatMap(
            (blockValues) =>
              Array.from(blockValues.values())
                .flatMap((layerTenants) => Array.from(layerTenants.values()))
                .map((registeredTenant) => ({
                  ...registeredTenant,
                  operations: registeredTenant.operations.filter(
                    (operation) => !operation.annulOperations.length,
                  ),
                }))
                .filter(({ operations }) => operations.length)
                .map(({ operations, isDraft }) => {
                  const latestOperation = operations.at(-1)
                  const internalOperationTenant =
                    latestOperation?.internalOperation.registeredTenant

                  assertNotEmpty(latestOperation)
                  assertNotEmpty(internalOperationTenant)

                  const isMatch =
                    internalOperationTenant.tenantId
                      ?.toLowerCase()
                      .includes(needle) ||
                    internalOperationTenant?.tenantName
                      ?.toLowerCase()
                      .includes(needle)

                  if (!isMatch) {
                    return null
                  }

                  return {
                    value: {
                      id: internalOperationTenant?.tenantId ?? '',
                      type: null,
                      operationType: 'tenant',
                      packageName: translate(
                        'tenantsTree.fallbackPackage.tenants',
                      ),
                      block: latestOperation.block,
                      layer: latestOperation.layer,
                      isDraft,
                    },
                  } satisfies ConfigurationItemSearchResult
                })
                .filter(notEmpty),
          ),
        )
      },
      [registeredTenantCreateOperations, translate],
    )

    const searchByClass = useCallback(() => {
      throw new Error('Searching tenants by class is unavailable')
    }, [])

    const searchByFPlus = useCallback(() => {
      throw new Error('Searching FPlus within tenants is unavailable')
    }, [])

    const searchByProperties = useCallback(() => {
      throw new Error('Searching tenants by properties is unavailable')
    }, [])

    const searchAll = useCallback(
      async (query: string) => {
        const allByName = await searchByName(query)

        return groupSearchResults(...allByName)
      },
      [searchByName],
    )

    return {
      name: searchByName,
      className: searchByClass,
      fplus: searchByFPlus,
      properties: searchByProperties,
      all: searchAll,
    }
  }
